import type { Editor } from '@tiptap/core';
import type { LiveblocksThread } from 'providers/CommentsContext';
import type { ThreadPosition, ThreadPositionManager } from './ThreadPositionManager';
import { DEFAULT_THREAD_POSITION, DEFAULT_THREAD_POSITION_TOP, THREAD_POSITION_OFFSET } from './ThreadPositionManager';

const getThreadPanelRect = (threadPanelRef: React.RefObject<HTMLElement>): DOMRect | null => {
  return threadPanelRef.current?.getBoundingClientRect() || null;
};

const calculatePosition = (
  editor: Editor,
  threadMarkPosition: number,
  panelRect: DOMRect,
  offset: number = THREAD_POSITION_OFFSET
): ThreadPosition => {
  if (!editor.state || !editor.state.doc) {
    return {
      top: DEFAULT_THREAD_POSITION_TOP,
      shown: true,
    };
  }

  const docSize = editor.state.doc.content.size;
  if (threadMarkPosition < 0 || threadMarkPosition > docSize) {
    return {
      top: DEFAULT_THREAD_POSITION_TOP,
      shown: true,
    };
  }

  try {
    const coords = editor.view.coordsAtPos(threadMarkPosition);
    const relativeTop = coords.top - panelRect.top - offset;

    return {
      top: Math.max(0, relativeTop),
      shown: true,
    };
  } catch (error) {
    return {
      top: DEFAULT_THREAD_POSITION_TOP,
      shown: true,
    };
  }
};

export const calculateThreadPosition = (
  positionManager: ThreadPositionManager,
  thread: LiveblocksThread,
  allEditors: Editor[],
  threadPanelRef: React.RefObject<HTMLElement>,
  threadMarkPosition: number | null,
  totalThreadsInGroup = 1,
  threadIndex?: number,
  isOpenedGroup?: boolean,
  isWithoutEditor?: boolean
) => {
  const manager = positionManager;
  let editor: Editor | null = null;

  if (isWithoutEditor) {
    return { top: (threadMarkPosition ?? 0) + DEFAULT_THREAD_POSITION_TOP * (threadIndex || 0), shown: true };
  }

  for (const currentEditor of allEditors) {
    const success = currentEditor.commands?.hasThread(thread.id);

    if (success) {
      editor = currentEditor;
      break;
    }
  }

  if (!editor || threadMarkPosition === null || threadMarkPosition < 0) {
    return DEFAULT_THREAD_POSITION;
  }

  const panelRect = getThreadPanelRect(threadPanelRef);
  if (!panelRect) {
    return DEFAULT_THREAD_POSITION;
  }

  const position = calculatePosition(editor, threadMarkPosition, panelRect);

  if (totalThreadsInGroup > 1 && threadIndex !== undefined) {
    position.top = manager.calculateGroupThreadPosition(
      position.top,
      threadIndex,
      totalThreadsInGroup,
      isOpenedGroup || false,
      threadMarkPosition
    );
  } else {
    position.top = manager.calculateSingleThreadPosition(position.top, thread.id);
  }

  return position;
};
